import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, TableShell, Drawer } from "@/components/ui-kit";
import DemoTenantDrawer from "@/components/DemoTenantDrawer";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";

const STATUS_DEFS = [
  { key: "trial", label: "Trial" },
  { key: "active", label: "Active" },
  { key: "past_due", label: "Past due" },
  { key: "canceled", label: "Canceled" },
  { key: "internal", label: "Internal" },
];
const STATUS_OPTS = STATUS_DEFS.map((s) => ({ value: s.key, label: s.label }));
const EMPTY_COUNTS = { trial: 0, active: 0, past_due: 0, canceled: 0, internal: 0 };

function fmtDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString();
}

function fmtLastActivity(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

function fmtPrice(cents) {
  const n = Number(cents || 0) / 100;
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export default function Tenants() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const [notesOpen, setNotesOpen] = useState(false);
  const [notesRow, setNotesRow] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [scopeRestrictDraft, setScopeRestrictDraft] = useState(false);
  const [scopeSourcesDraft, setScopeSourcesDraft] = useState([]);

  const [sandboxRows, setSandboxRows] = useState([]);
  const [sandboxLoading, setSandboxLoading] = useState(false);
  const [newSandboxOpen, setNewSandboxOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    return api
      .get("/platform/tenants")
      .then((r) => {
        setRows(r.data?.rows || []);
        setCounts(r.data?.counts || EMPTY_COUNTS);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadSandboxes = useCallback(() => {
    setSandboxLoading(true);
    return api
      .get("/platform/demo-tenants")
      .then((r) => setSandboxRows(r.data?.rows || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setSandboxLoading(false));
  }, []);

  useEffect(() => {
    loadSandboxes();
  }, [loadSandboxes]);

  const sandboxAgencyIds = useMemo(() => new Set(sandboxRows.map((s) => s.agency_id)), [sandboxRows]);

  const refreshAfterSandboxChange = () => {
    loadSandboxes();
    load();
  };

  const purgeSandbox = async (agencyId) => {
    if (!window.confirm("Purge this sandbox and everything in it?")) return;
    try {
      await api.delete(`/platform/demo-tenants/${agencyId}`);
      toast.success("Sandbox purged");
      refreshAfterSandboxChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const visibleRows = useMemo(
    () => (statusFilter ? rows.filter((t) => t.plan?.status === statusFilter) : rows),
    [rows, statusFilter]
  );

  const toggleTile = (key) => {
    setStatusFilter((cur) => (cur === key ? "" : key));
  };

  const mergeRow = (updated) => {
    setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/platform/tenants/${id}`, { status });
      mergeRow(data);
      toast.success("Plan updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const updateTrialEnds = async (id, value) => {
    try {
      const { data } = await api.patch(`/platform/tenants/${id}`, { trial_ends_at: value });
      mergeRow(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openNotes = (row) => {
    setNotesRow(row);
    setNotesDraft(row.plan?.notes || "");
    const scope = row.plan?.import_scope || [];
    setScopeRestrictDraft(scope.length > 0);
    setScopeSourcesDraft(scope);
    setNotesOpen(true);
  };

  const toggleScopeSource = (key) => {
    setScopeSourcesDraft((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  };

  const saveNotes = async () => {
    if (!notesRow) return;
    if (scopeRestrictDraft && scopeSourcesDraft.length === 0) {
      toast.error("Pick at least one import source, or turn the restriction off");
      return;
    }
    setSavingNotes(true);
    try {
      const { data } = await api.patch(`/platform/tenants/${notesRow.id}`, {
        notes: notesDraft,
        import_scope: scopeRestrictDraft ? scopeSourcesDraft : [],
      });
      mergeRow(data);
      setNotesRow(data);
      toast.success("Tenant settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <div data-testid="tenants-page">
      <PageHeader subtitle="Every agency on the platform" title="Tenants" />

      <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-5">
        {STATUS_DEFS.map((s) => {
          const active = statusFilter === s.key;
          return (
            <button
              key={s.key}
              onClick={() => toggleTile(s.key)}
              className={`text-left rounded-md border p-3 transition ${
                active ? "bg-[#0A192F] border-[#0A192F]" : "bg-white border-slate-200 hover:border-slate-400"
              }`}
              data-testid={`tenant-tile-${s.key}`}
            >
              <div className={`text-[10px] font-semibold uppercase tracking-[0.1em] ${active ? "text-slate-300" : "text-slate-500"}`}>
                {s.label}
              </div>
              <div className={`font-display text-xl font-bold mt-1 ${active ? "text-white" : "text-[#0A192F]"}`}>
                {counts[s.key] ?? 0}
              </div>
            </button>
          );
        })}
      </div>

      <TableShell
        columns={["Agency", "Plan", "Trial ends", "Seats", "Rides 30d", "Last activity", "Price", "Notes"]}
        empty={!loading && visibleRows.length === 0 ? "No tenants yet." : null}
      >
        {visibleRows.map((t) => (
          <tr key={t.id} className="tbl-row border-t border-slate-100" data-testid={`tenant-row-${t.id}`}>
            <td className="px-4 py-2.5">
              <div className="flex items-center gap-2">
                <div className="font-medium text-slate-900">{t.name}</div>
                {(t.plan?.notes || "").startsWith("Prospect demo sandbox") || sandboxAgencyIds.has(t.id) ? (
                  <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full border bg-slate-100 text-slate-600 border-slate-200">
                    sandbox
                  </span>
                ) : null}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{fmtDate(t.created_at)}</div>
              {t.admins && t.admins.length > 0 && (
                <div className="text-xs text-slate-400 mt-0.5">{t.admins.join(", ")}</div>
              )}
            </td>
            <td className="px-4 py-2.5">
              <select
                value={t.plan?.status || "trial"}
                onChange={(e) => updateStatus(t.id, e.target.value)}
                className="text-xs px-2 py-1.5 rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-300"
                data-testid={`tenant-status-${t.id}`}
              >
                {STATUS_OPTS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </td>
            <td className="px-4 py-2.5">
              <input
                key={`trial-${t.id}-${t.plan?.trial_ends_at || ""}`}
                type="date"
                defaultValue={t.plan?.trial_ends_at ? t.plan.trial_ends_at.slice(0, 10) : ""}
                onChange={(e) => updateTrialEnds(t.id, e.target.value)}
                className="text-xs px-2 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
                data-testid={`tenant-trial-${t.id}`}
              />
              {t.locked ? (
                <div className="mt-1">
                  <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-rose-50 text-rose-800 border-rose-200">
                    locked
                  </span>
                </div>
              ) : t.plan?.status === "trial" && typeof t.days_left === "number" ? (
                <div className="text-[11px] text-slate-500 mt-1">{t.days_left} day{t.days_left === 1 ? "" : "s"} left</div>
              ) : null}
            </td>
            <td className="px-4 py-2.5 text-xs">
              <div className="text-sm text-slate-900">{t.seats ?? 0}</div>
              <div className="text-slate-500 mt-0.5">of {t.drivers ?? 0} drivers</div>
            </td>
            <td className="px-4 py-2.5 text-xs">{t.rides_30d ?? 0}</td>
            <td className="px-4 py-2.5 text-xs whitespace-nowrap">{fmtLastActivity(t.last_activity)}</td>
            <td className="px-4 py-2.5 text-xs">{fmtPrice(t.plan?.seat_price_cents)}</td>
            <td className="px-4 py-2.5">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openNotes(t)}
                  className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-400"
                  data-testid={`tenant-notes-${t.id}`}
                >
                  Settings
                </button>
                {(t.plan?.import_scope || []).length > 0 && (
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"
                    title={`Restricted to: ${t.plan.import_scope.join(", ")}`}
                  >
                    scoped
                  </span>
                )}
              </div>
            </td>
          </tr>
        ))}
      </TableShell>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-bold text-[#0A192F]">Prospect sandboxes</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={loadSandboxes}
              className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-400 flex items-center gap-1.5"
              data-testid="demo-tenant-refresh"
            >
              <RefreshCw size={12} className={sandboxLoading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={() => setNewSandboxOpen(true)}
              className="btn-primary"
              data-testid="demo-tenant-new"
            >
              + New sandbox
            </button>
          </div>
        </div>

        <TableShell
          columns={["Name", "Admin email", "Status", "Expires", "Created", "Actions"]}
          empty={
            !sandboxLoading && sandboxRows.length === 0
              ? "No prospect sandboxes. Create one from the Prospects page."
              : null
          }
        >
          {sandboxRows.map((s) => {
            const failed = (s.status || "").startsWith("failed");
            const statusPillClass = failed
              ? "bg-rose-50 text-rose-800 border-rose-200"
              : s.status === "ready"
              ? "bg-emerald-50 text-emerald-800 border-emerald-200"
              : "bg-amber-50 text-amber-800 border-amber-200";
            return (
              <tr key={s.agency_id} className="tbl-row border-t border-slate-100" data-testid={`demo-tenant-row-${s.agency_id}`}>
                <td className="px-4 py-2.5 font-medium text-slate-900">{s.name}</td>
                <td className="px-4 py-2.5 text-xs text-slate-600">{s.admin_email || "-"}</td>
                <td className="px-4 py-2.5">
                  <span
                    title={failed ? s.status : undefined}
                    className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${statusPillClass}`}
                  >
                    {s.status === "seeding" && <Loader2 size={10} className="animate-spin" />}
                    {failed ? "failed" : s.status || "-"}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-xs">
                  <div>{fmtDate(s.expires_at)}</div>
                  {s.locked ? (
                    <div className="text-[11px] text-rose-600 mt-0.5">expired</div>
                  ) : typeof s.days_left === "number" ? (
                    <div className="text-[11px] text-slate-500 mt-0.5">{s.days_left} day{s.days_left === 1 ? "" : "s"} left</div>
                  ) : null}
                </td>
                <td className="px-4 py-2.5 text-xs">
                  <div>{fmtDate(s.created_at)}</div>
                  {s.created_by && <div className="text-slate-400 mt-0.5">{s.created_by}</div>}
                </td>
                <td className="px-4 py-2.5">
                  <button
                    onClick={() => purgeSandbox(s.agency_id)}
                    className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:border-rose-400 hover:text-rose-700"
                    data-testid={`demo-tenant-purge-${s.agency_id}`}
                  >
                    Purge now
                  </button>
                </td>
              </tr>
            );
          })}
        </TableShell>
      </div>

      <DemoTenantDrawer
        open={newSandboxOpen}
        onClose={() => setNewSandboxOpen(false)}
        prospect={null}
        onCreated={refreshAfterSandboxChange}
      />

      <Drawer
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        title="Tenant settings"
        footer={
          <>
            <button onClick={() => setNotesOpen(false)} className="text-sm px-3 py-2 rounded-md border border-slate-200">
              Close
            </button>
            <button onClick={saveNotes} disabled={savingNotes} className="btn-primary" data-testid="tenant-notes-save">
              Save
            </button>
          </>
        }
      >
        {notesRow && (
          <div className="space-y-4">
            <div className="text-sm text-slate-900 font-medium">{notesRow.name}</div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Notes</label>
              <textarea
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                rows={6}
                className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Import sources</label>
              <label className="mt-2 flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={scopeRestrictDraft}
                  onChange={(e) => setScopeRestrictDraft(e.target.checked)}
                  data-testid="tenant-scope-restrict"
                />
                Restrict this tenant to specific import sources
              </label>
              {scopeRestrictDraft && (
                <div className="mt-2 space-y-1.5 pl-1">
                  {[
                    { key: "avo", label: "Avo (broker POs)" },
                    { key: "gott", label: "Go T&T (broker POs)" },
                    { key: "csv", label: "Payer CSV import (e.g. UCare)" },
                  ].map((s) => (
                    <label key={s.key} className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={scopeSourcesDraft.includes(s.key)}
                        onChange={() => toggleScopeSource(s.key)}
                        data-testid={`tenant-scope-${s.key}`}
                      />
                      {s.label}
                    </label>
                  ))}
                </div>
              )}
              <div className="text-xs text-slate-400 mt-1.5">
                {scopeRestrictDraft
                  ? "Any other import source is blocked with a 403 for this tenant."
                  : "Unrestricted: this tenant can use every import source."}
              </div>
            </div>
            <div className="text-xs text-slate-400">
              {notesRow.plan?.updated_by_email
                ? `Last updated ${fmtDate(notesRow.plan.updated_at)} by ${notesRow.plan.updated_by_email}`
                : "No updates yet."}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
